'use client';

import { useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  storeSessionParams,
  readSessionParams,
  isForwardableInternalLink,
  withForwardedParams,
} from '@/lib/utils/sessionParams';

/**
 * SessionParamForwarder
 *
 * A zero-render, side-effect-only component mounted once in the root layout.
 * It ensures URL parameters captured on any landing page (PLP or otherwise)
 * are appended to every subsequent internal link click for the full tab session.
 *
 * Behaviour:
 * - On mount: if the current page URL has params, writes them to sessionStorage
 *   using a latest-write-wins policy (overwrites params from a prior landing).
 * - Attaches document-level capturing event listeners to intercept all internal
 *   anchor clicks and rewrite the destination URL with the stored params.
 * - External links, tel:, mailto:, and fragment-only links are never touched.
 * - Cleans up all listeners and restores original hrefs on unmount.
 *
 * This component works alongside PLPLocalizationProvider: the PLP provider
 * handles first-page PLP forwarding (same mechanism, same helpers), while this
 * component picks up the torch on every page the user navigates to thereafter.
 */
export default function SessionParamForwarder() {
  const router = useRouter();
  const searchParams = useSearchParams();

  // ── Write phase: capture any params from the current landing URL ──────────
  useEffect(() => {
    const currentParams = new URLSearchParams(searchParams.toString());
    if (currentParams.toString() !== '') {
      storeSessionParams(currentParams);
    }
  }, [searchParams]);

  // ── Forward phase: intercept internal clicks and append stored params ──────
  useEffect(() => {
    // Track original hrefs so we can restore them on cleanup and handle
    // middle-click / right-click / context-menu correctly.
    const originalHrefs = new Map<HTMLAnchorElement, string>();

    /**
     * Just-in-time DOM href rewrite — fires before the browser reads the href
     * attribute for middle-click, right-click "copy link address", or Next.js navigation.
     */
    const syncHref = (event: Event) => {
      const stored = readSessionParams();
      if (stored.toString() === '') return;

      const anchor = (event.target as HTMLElement | null)?.closest('a');
      if (!anchor || !isForwardableInternalLink(anchor)) return;

      if (!originalHrefs.has(anchor)) {
        originalHrefs.set(anchor, anchor.getAttribute('href') as string);
      }

      anchor.href = withForwardedParams(originalHrefs.get(anchor)!, stored).toString();
    };

    /**
     * Intercepts left-click navigation and calls router.push() with params,
     * preventing Next.js's own handler from navigating without them.
     */
    const onClick = (event: MouseEvent) => {
      const stored = readSessionParams();
      if (stored.toString() === '') return;

      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const anchor = (event.target as HTMLElement | null)?.closest('a');
      if (!anchor) return;
      if (anchor.target && anchor.target !== '_self') return;
      if (!isForwardableInternalLink(anchor)) return;

      const destination = withForwardedParams(
        originalHrefs.get(anchor) ?? (anchor.getAttribute('href') as string),
        stored,
      );

      event.preventDefault();
      event.stopImmediatePropagation();
      router.push(`${destination.pathname}${destination.search}${destination.hash}`);
    };

    document.addEventListener('pointerdown', syncHref, true);
    document.addEventListener('pointerover', syncHref, true);
    document.addEventListener('focus', syncHref, true);
    document.addEventListener('contextmenu', syncHref, true);
    document.addEventListener('click', onClick, true);

    return () => {
      document.removeEventListener('pointerdown', syncHref, true);
      document.removeEventListener('pointerover', syncHref, true);
      document.removeEventListener('focus', syncHref, true);
      document.removeEventListener('contextmenu', syncHref, true);
      document.removeEventListener('click', onClick, true);
      originalHrefs.forEach((original, anchor) => anchor.setAttribute('href', original));
    };
  }, [router]);

  return null;
}
